import React from "react";
import { GsdReadableBlock } from "../../components/GsdReadableBlock.jsx";
import { resolveStandaloneModuleLabel, resolveStandaloneModuleTabPath } from "./createStandaloneModuleConfig.js";

/**
 * Sous-navigation interne d'un module GSD à part.
 * @param {object} props
 * @param {string} props.basePath
 * @param {Array<object>} props.tabs
 * @param {'fr'|'en'} [props.lang]
 * @param {string} [props.activeTab] — id tab actif (mode sans router)
 * @param {(tab: object, ctx: { className: string, children: React.ReactNode, href: string, isActive: boolean, disabled: boolean }) => React.ReactNode} [props.renderTabLink]
 * @param {'horizontal'|'grid'} [props.orientation]
 * @param {string} [props.testId]
 * @param {string} [props.ariaLabelFr]
 * @param {string} [props.ariaLabelEn]
 */
export function GsdStandaloneModuleSubNav({
  basePath,
  tabs = [],
  lang = "fr",
  activeTab,
  renderTabLink,
  orientation = "horizontal",
  testId = "gsd-standalone-module-subnav",
  ariaLabelFr = "Navigation interne du module",
  ariaLabelEn = "Module internal navigation",
}) {
  const locale = lang === "en" ? "en" : "fr";
  const ariaLabel = locale === "en" ? ariaLabelEn : ariaLabelFr;

  return (
    <GsdReadableBlock testId={testId} className="gsd-standalone-module-subnav-wrap" variant="default">
      <nav
        className={`gsd-standalone-module-subnav gsd-standalone-module-subnav--${orientation}`}
        aria-label={ariaLabel}
      >
        {tabs.map((tab) => {
          const href = resolveStandaloneModuleTabPath(basePath, tab);
          const label = resolveStandaloneModuleLabel(locale, tab);
          const disabled = tab.status === "disabled" || tab.status === "future";
          const isActive = activeTab ? activeTab === tab.id : false;
          const className = [
            "gsd-standalone-module-subnav__link",
            isActive ? "gsd-standalone-module-subnav__link--active" : "",
            disabled ? "gsd-standalone-module-subnav__link--disabled" : "",
            tab.status === "future" ? "gsd-standalone-module-subnav__link--future" : "",
          ]
            .filter(Boolean)
            .join(" ");

          const children = (
            <>
              {tab.icon ? <span className="gsd-standalone-module-subnav__icon" aria-hidden="true">{tab.icon}</span> : null}
              <span>{label}</span>
              {tab.status === "future" ? (
                <span className="gsd-standalone-module-subnav__badge">{locale === "en" ? "Soon" : "Bientôt"}</span>
              ) : null}
            </>
          );

          if (renderTabLink) {
            return (
              <React.Fragment key={tab.id}>
                {renderTabLink(tab, { className, children, href, isActive, disabled })}
              </React.Fragment>
            );
          }

          if (disabled) {
            return (
              <span
                key={tab.id}
                className={className}
                aria-disabled="true"
                data-testid={tab.testId}
              >
                {children}
              </span>
            );
          }

          return (
            <a key={tab.id} href={href} className={className} data-testid={tab.testId}>
              {children}
            </a>
          );
        })}
      </nav>
    </GsdReadableBlock>
  );
}
